import React from "react"
import styled, { withTheme } from "styled-components"
import { connect } from "react-redux"
import { HeadingTwo, Label, Paragraph, ButtonLink, Button, ErrorMessage } from "buoy-core"
import {
  CardElement,
  Elements,
  injectStripe,
  PaymentRequestButtonElement,
} from "react-stripe-elements"
import { colors } from "buoy-theme"
import { makePayment, setSaved, samlHandoff, makeButtonPayment } from "../actions"
import FixedPaymentFooter from "./FixedPaymentFooter"
import { Page, BreadCrumbContainer, Content } from "./Page"
import { IconRow, StripeBadge, Lock } from "./IconRow"
import { ButtonRow } from "./ButtonRow"
import BreadcrumbNav from "./BreadcrumbNav"
import tracker from "../../common/tracker"

const paidMessage = "You have already paid for this visit."

const paymentMessage = "Enter your payment details to continue with Buoy LIVE."

const CardElementWrapper = styled.div`
  background-color: ${colors.neutralLightest};
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: ${props => props.canMakePayment && props.errorMsg.length === 0 ? "12px" : 0};
`

const SavedRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 38px 0 0;
`

const getFormStyle = theme => ({
  base: {
    color: theme.colors.neutralDarkest,
    fontSize: "16px",
    fontFamily: theme.fonts.fontFamily,
    fontWeight: theme.fonts.weight.light,
    width: "100%",
  },
})

const PaidContent = styled.div`
  background: ${colors.neutralLightest};
  border-radius: 4px;
  padding: 36px 16px 12px;
`

const ParagraphWithMargin = styled(Paragraph)`
  margin: 0 0 36px;
`

const PaidParagraph = () => (
  <ParagraphWithMargin center>{paidMessage}</ParagraphWithMargin>
)

const Paid = ({ samlHandoff }) => (
  <Content>
    <HeadingTwo margin="0 0 13px" alignLeft>
      Payment
    </HeadingTwo>
    <PaidContent>
      <PaidParagraph />
      <ButtonRow>
        <Button onClick={samlHandoff}>Proceed to Visit</Button>
      </ButtonRow>
    </PaidContent>
  </Content>
)

const paymentBtnStyle = {
  paymentRequestButton: {
    theme: "light-outline",
    height: "42px",
  },
}

export const SecurityIcons = ({saved=false, center=false}) => (
  <IconRow saved={saved} center={center}>
    <StripeBadge />
    <Lock />
  </IconRow>
)

export const CreditCardInputForm = ({disabled=false, canMakePayment, theme, errorMsg='', onChange=()=>{}}) => (
  <div>
    <CardElementWrapper canMakePayment={canMakePayment} errorMsg={errorMsg}>
      <CardElement disabled={disabled} style={getFormStyle(theme)} onChange={onChange}/>
    </CardElementWrapper>
    { errorMsg && <ErrorMessage position="relative">{errorMsg.msg || errorMsg}</ErrorMessage>}
  </div>
)

export const ApplePayBtn = ({label, paymentRequest, paymentButtonStyle=paymentBtnStyle}) => (
  <div>
    <Paragraph center color="neutralLight" fontSize="bodySmall">{label}</Paragraph>
    <PaymentRequestButtonElement
      paymentRequest={paymentRequest}
      className="PaymentRequestButton"
      style={paymentButtonStyle}
    />
  </div>
)

const PaymentForm = ({
  theme,
  makePayment,
  paid,
  stripe,
  saved,
  clearSaved,
  integration,
  paymentRequest,
  canMakePayment,
  paymentErrorMsg,
}) => (
  <Content>
    <HeadingTwo margin="0 0 13px" alignLeft>
      Payment
    </HeadingTwo>
    <Paragraph margin="0">{paymentMessage}</Paragraph>
    {saved ? (
      <SavedRow>
        <Paragraph margin="0">
          {saved.brand} ending in {saved.last4}
        </Paragraph>
        <ButtonLink onClick={clearSaved}>Change</ButtonLink>
      </SavedRow>
    ) : (
      <div>
        <Label>Card information</Label>
        <CreditCardInputForm 
          canMakePayment={canMakePayment} 
          disabled={paid} 
          theme={theme}
          errorMsg={paymentErrorMsg} />
        {canMakePayment && (
          <ApplePayBtn paymentRequest={paymentRequest} label="or pay with"/> )}
      </div>
    )}
    <SecurityIcons saved={saved} />
    <FixedPaymentFooter
      buttonText="Place order"
      heading="Order Summary"
      item="Buoy LIVE, Powered by CirrusMD"
      onClick={makePayment(stripe)}
      price={integration.price}
    />
  </Content>
)

class WrappedPaymentForm extends React.Component {
  state = {
    canMakePayment: false,
    paymentRequest: null,
  }

  componentDidMount() {
    const paymentRequest = this.props.stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: {
        label: "Buoy LIVE, Powered by CirrusMD",
        amount: Number(this.props.integration.price) * 100,
      },
      requestPayerName: true,
      requestPayerPhone: true
    })

    paymentRequest.on("token", ({ complete, token }) => {
      this.props.makeButtonPayment(token).then(() => complete("success"))
    })

    paymentRequest.canMakePayment().then(result => {
      this.setState({ canMakePayment: result ? true : false, paymentRequest })
    })

    tracker.paymentCardPageLoaded()
  }

  render() {
    return <PaymentForm {...this.props} {...this.state} />
  }
}

const InjectedWrappedPaymentForm = injectStripe(WrappedPaymentForm)

const Payment = props => (
  <Page>
    <BreadCrumbContainer>
      <BreadcrumbNav />
      <Elements>
        {props.paid ? (
          <Paid samlHandoff={props.samlHandoff} />
        ) : (
          <InjectedWrappedPaymentForm {...props} />
        )}
      </Elements>
    </BreadCrumbContainer>
  </Page>
)

const mapStateToProps = state => ({
  paid: state.payment.paid,
  saved: state.payment.saved,
  integration: state.payment.integration,
  paymentErrorMsg: state.payment.paymentErrorMsg,
})

const mapDispatchToProps = dispatch => ({
  makePayment: stripe => () => dispatch(makePayment(stripe)),
  makeButtonPayment: token => dispatch(makeButtonPayment(token)),
  clearSaved: () => dispatch(setSaved(null)),
  samlHandoff: () => dispatch(samlHandoff()),
})

export default withTheme(
  connect(mapStateToProps, mapDispatchToProps)(Payment)
)



// WEBPACK FOOTER //
// ./assets/payment/components/Payment.js